import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Percent, Plus, Building2 } from 'lucide-react';
import { getRebatePrograms, createRebateProgram } from '../../config/api';

const PAYER_TYPES = ['PBM', 'HEALTH_PLAN', 'EMPLOYER', 'MEDICARE_PART_D', 'OTHER'];

const emptyForm = { programNumber: '', name: '', payerName: '', payerType: 'PBM', basis: 'SALES', startDate: '', endDate: '', notes: '' };

export default function Rebates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');

  const { data: programs = [], isLoading, error } = useQuery({
    queryKey: ['rebate-programs'],
    queryFn: getRebatePrograms,
  });

  const createMut = useMutation({
    mutationFn: createRebateProgram,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['rebate-programs'] });
      setShowForm(false);
      setForm(emptyForm);
      if (created?.id) navigate(`/app/rebates/${created.id}`);
    },
    onError: (e) => setFormError(e?.response?.data?.error || 'Failed to create program'),
  });

  const submit = (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.programNumber.trim() || !form.name.trim() || !form.payerName.trim()) {
      setFormError('Program number, name, and payer name are required.');
      return;
    }
    createMut.mutate(form);
  };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Percent className="w-6 h-6 text-primary-600" /> Commercial Rebates
          </h1>
          <p className="text-gray-500 mt-1">Managed-care / PBM rebate programs — a gross-to-net component alongside chargebacks and GPO admin fees.</p>
        </div>
        <button onClick={() => { setShowForm((s) => !s); setFormError(''); }} className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          <Plus className="w-4 h-4" /> New Program
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-lg shadow p-5 mb-6 no-card-hover">
          <h2 className="font-semibold text-gray-800 mb-4">New Rebate Program</h2>
          {formError && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{formError}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Program Number *</label>
              <input value={form.programNumber} onChange={setField('programNumber')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g. REB-CVS-2026" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Program Name *</label>
              <input value={form.name} onChange={setField('name')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g. CVS Caremark Formulary Rebate" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payer Name *</label>
              <input value={form.payerName} onChange={setField('payerName')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g. CVS Caremark" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payer Type</label>
              <select value={form.payerType} onChange={setField('payerType')} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                {PAYER_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Rate Basis</label>
              <select value={form.basis} onChange={setField('basis')} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="SALES">Eligible Sales ($)</option>
                <option value="WAC">WAC</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input type="date" value={form.startDate} onChange={setField('startDate')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input type="date" value={form.endDate} onChange={setField('endDate')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button type="submit" disabled={createMut.isPending} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
              {createMut.isPending ? 'Creating…' : 'Create Program'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setForm(emptyForm); }} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
          </div>
        </form>
      )}

      {isLoading && <div className="text-gray-500">Loading programs…</div>}
      {error && <div className="text-red-600">Failed to load rebate programs.</div>}

      {!isLoading && !error && programs.length === 0 && (
        <div className="bg-white rounded-lg shadow p-10 text-center text-gray-500 no-card-hover">
          No rebate programs yet. Create one to start accruing commercial rebates.
        </div>
      )}

      {programs.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden no-card-hover">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Program</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tiers</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Accruals</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {programs.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/app/rebates/${p.id}`)}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500">{p.programNumber}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700"><span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5 text-gray-400" />{p.payerName}</span></td>
                  <td className="px-4 py-3 text-gray-600">{(p.payerType || '').replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">{p._count?.tiers ?? 0}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{p._count?.accruals ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
